import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import SideBar from './SideBar';
import HeaderSection from './HeaderSection';
import { useAuth } from '../../context/AuthContext';
import {
    getCustomerList,
    impersonateCustomer,
    getCustomerPlanHistory,
    getCustomerWidgetUsage,
} from '../../utils/admin/dashboardApi';
import { ADMIN_CUSTOMER_APP_SHORTCUTS } from '../../data/adminCustomerAppShortcuts';
import { getAdminRouteForAppType } from '../../data/adminAppTypeToCustomerRoute';

const ADMIN_USAGE_MODAL_REOPEN_KEY = 'admin_usage_modal_reopen_customer';

export default function Customers() {
    const navigate = useNavigate();
    const location = useLocation();
    const allCustomersInFlight = useRef(false);
    const { startImpersonationSession } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setIsLoading] = useState(true);
    const [allCustomersLoading, setAllCustomersLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [allCustomers, setAllCustomers] = useState([]);
    const [hasLoadedAllCustomers, setHasLoadedAllCustomers] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [planFilter, setPlanFilter] = useState('All');
    const [allPlans, setAllPlans] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showPlanHistoryModal, setShowPlanHistoryModal] = useState(false);
    const [planHistoryRows, setPlanHistoryRows] = useState([]);
    const [planHistoryLoading, setPlanHistoryLoading] = useState(false);
    const [planHistoryError, setPlanHistoryError] = useState(null);
    const [impersonatingId, setImpersonatingId] = useState(null);
    const [showUsageExplorer, setShowUsageExplorer] = useState(false);
    const [usageCustomer, setUsageCustomer] = useState(null);
    const [usageOpeningAppKey, setUsageOpeningAppKey] = useState(null);
    const [usageAppFilter, setUsageAppFilter] = useState('');
    const [showAllShortcutApps, setShowAllShortcutApps] = useState(false);
    const [usageDetailLoading, setUsageDetailLoading] = useState(false);
    const [usageDetailError, setUsageDetailError] = useState(null);
    const [usageDetail, setUsageDetail] = useState(null);
    const limit = 25;

    useEffect(() => {
        if (!showUsageExplorer) {
            setUsageAppFilter('');
            setShowAllShortcutApps(false);
        }
    }, [showUsageExplorer]);

    useEffect(() => {
        if (!showUsageExplorer || !usageCustomer?.id) {
            setUsageDetail(null);
            setUsageDetailError(null);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setUsageDetailLoading(true);
            setUsageDetailError(null);
            try {
                const res = await getCustomerWidgetUsage(usageCustomer.id);
                if (cancelled) return;
                if (res?.status === true && res?.data) {
                    setUsageDetail(res.data);
                } else {
                    setUsageDetail(null);
                    setUsageDetailError(res?.message || 'Could not load widget usage.');
                }
            } catch (err) {
                if (!cancelled) {
                    setUsageDetail(null);
                    setUsageDetailError(err?.message || 'Could not load widget usage.');
                }
            } finally {
                if (!cancelled) setUsageDetailLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [showUsageExplorer, usageCustomer?.id]);

    const filteredAppShortcuts = useMemo(() => {
        const q = usageAppFilter.trim().toLowerCase();
        if (!q) return ADMIN_CUSTOMER_APP_SHORTCUTS;
        return ADMIN_CUSTOMER_APP_SHORTCUTS.filter(
            (a) =>
                a.label.toLowerCase().includes(q) ||
                a.customerPath.toLowerCase().includes(q) ||
                a.key.toLowerCase().includes(q)
        );
    }, [usageAppFilter]);
    const filteredUsageApps = useMemo(() => {
        const list = Array.isArray(usageDetail?.by_app) ? usageDetail.by_app : [];
        const q = usageAppFilter.trim().toLowerCase();
        if (!q) return list;
        return list.filter((row) => {
            const meta = getAdminRouteForAppType(row.app_type);
            return (
                String(row.app_type || '').toLowerCase().includes(q) ||
                String(meta.label || '').toLowerCase().includes(q) ||
                String(meta.customerPath || '').toLowerCase().includes(q)
            );
        });
    }, [usageDetail, usageAppFilter]);
    const brandColors = {
        logoBlueDark: '#16182D',
        logoBlue: '#1f3f95',
        logoBlueLight: '#5f79c7',
        logoYellow: '#f5c400',
        borderSoft: '#dcdcdc',
        surfaceSoft: '#f6f6f6',
        activeGreen: '#35A800',
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        return new Date(dateValue).toLocaleDateString();
    };

    const customerWidgetTotals = (c) => ({
        widgets: Number(c?.widgets_total ?? c?.widgetsTotal ?? 0) || 0,
        appTypes: Number(c?.app_types_count ?? c?.appTypesCount ?? 0) || 0,
    });

    const planHistoryStatusSuccess = (status) => {
        const t = String(status || '').toLowerCase();
        return t.includes('success') || t === 'paid' || t === 'completed' || t === 'succeeded';
    };

    useEffect(() => {
        if (!showPlanHistoryModal || !selectedCustomer?.id) {
            setPlanHistoryRows([]);
            setPlanHistoryError(null);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setPlanHistoryLoading(true);
            setPlanHistoryError(null);
            try {
                const res = await getCustomerPlanHistory(selectedCustomer.id);
                if (cancelled) return;
                if (res?.status === true) {
                    setPlanHistoryRows(Array.isArray(res.data) ? res.data : []);
                } else {
                    setPlanHistoryRows([]);
                    setPlanHistoryError(res?.message || 'Could not load plan history.');
                }
            } catch (err) {
                if (!cancelled) {
                    setPlanHistoryRows([]);
                    setPlanHistoryError(err?.message || 'Could not load plan history.');
                    toast.error(err?.message || 'Could not load plan history.');
                }
            } finally {
                if (!cancelled) setPlanHistoryLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [showPlanHistoryModal, selectedCustomer?.id]);

    // Fetch Customer data
    const fetchCustomers = async (currentPage, currentLimit) => {
        setIsLoading(true);
        try {
            const result = await getCustomerList(currentPage, currentLimit);
            if (result && result.data) {
                setCustomers(result.data.customers || []);
                setTotalCustomers(result.data.total || 0);
                setTotalPages(result.data.totalPages);
            }
        } catch (error) {
            toast.error(`${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers(page, limit);
    }, [page, limit]);

    const isFilterActive = Boolean(searchTerm) || statusFilter !== 'All' || planFilter !== 'All';

    const fetchAllCustomers = async () => {
        if (allCustomersInFlight.current) return;
        allCustomersInFlight.current = true;
        setAllCustomersLoading(true);
        try {
            const firstPage = await getCustomerList(1, limit);
            const firstCustomers = firstPage?.data?.customers || [];
            const allTotalPages = firstPage?.data?.totalPages || 1;

            let merged = [...firstCustomers];
            if (allTotalPages > 1) {
                const pagePromises = [];
                for (let p = 2; p <= allTotalPages; p += 1) {
                    pagePromises.push(getCustomerList(p, limit));
                }
                const pageResponses = await Promise.all(pagePromises);
                pageResponses.forEach((response) => {
                    merged = merged.concat(response?.data?.customers || []);
                });
            }
            setAllCustomers(merged);
            setHasLoadedAllCustomers(true);
        } catch (error) {
            toast.error(error.message || 'Failed to load all customers for filtering.');
        } finally {
            allCustomersInFlight.current = false;
            setAllCustomersLoading(false);
        }
    };

    /* Full customer list for filters — reload when visiting this route (avoids stale rows after API deploy). */
    useEffect(() => {
        if (!location.pathname.includes('all-customers')) return;
        fetchAllCustomers();
    }, [location.pathname]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, statusFilter, planFilter]);

    useEffect(() => {
        const fetchAllPlans = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/all-plan-list`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                const result = await response.json();
                if (result?.status === true && Array.isArray(result?.data)) {
                    const planNames = result.data.map((plan) => plan.name).filter(Boolean);
                    setAllPlans(Array.from(new Set(planNames)));
                }
            } catch (error) {
                // Fallback to current list plans if API fails.
                setAllPlans([]);
            }
        };

        fetchAllPlans();
    }, []);

    const handlePrev = () => page > 1 && setPage(page - 1);
    const handleNext = () => page < effectiveTotalPages && setPage(page + 1);

    const openUsageExplorer = (customer) => {
        setUsageCustomer(customer);
        setUsageOpeningAppKey(null);
        setShowAllShortcutApps(false);
        setShowUsageExplorer(true);
    };

    const closeUsageExplorer = () => {
        setShowUsageExplorer(false);
        setUsageCustomer(null);
        setUsageOpeningAppKey(null);
    };

    const handleOpenCustomerAppPage = async (app) => {
        if (!usageCustomer?.id) return;
        if (!app?.customerPath) {
            toast.error('This app type is not linked to a customer portal page yet.');
            return;
        }
        if (usageCustomer.status !== 'Active') {
            toast.error('Only active customers can be opened in their app dashboard.');
            return;
        }
        setUsageOpeningAppKey(app.key);
        try {
            const result = await impersonateCustomer(usageCustomer.id);
            if (result?.status === true && result?.data) {
                sessionStorage.setItem(
                    ADMIN_USAGE_MODAL_REOPEN_KEY,
                    JSON.stringify({
                        id: usageCustomer.id,
                        fname: usageCustomer.fname || '',
                        lname: usageCustomer.lname || '',
                        email: usageCustomer.email || '',
                        status: usageCustomer.status || '',
                    })
                );
                toast.success(`Opening ${app.label} for ${usageCustomer.fname || 'customer'}…`);
                closeUsageExplorer();
                startImpersonationSession(result, navigate, app.customerPath);
            } else {
                toast.error(result?.message || 'Could not open customer view.');
            }
        } catch (err) {
            toast.error(err?.message || 'Could not open customer view.');
        } finally {
            setUsageOpeningAppKey(null);
        }
    };

    useEffect(() => {
        if (!location.pathname.includes('all-customers')) return;
        const raw = sessionStorage.getItem(ADMIN_USAGE_MODAL_REOPEN_KEY);
        if (!raw) return;

        try {
            const parsed = JSON.parse(raw);
            if (!parsed?.id) return;
            setUsageCustomer(parsed);
            setUsageOpeningAppKey(null);
            setShowUsageExplorer(true);
        } catch {
            // Ignore malformed saved state.
        } finally {
            sessionStorage.removeItem(ADMIN_USAGE_MODAL_REOPEN_KEY);
        }
    }, [location.pathname]);

    const handleViewAsCustomer = async (customer) => {
        if (!customer?.id) return;
        if (customer.status !== 'Active') {
            toast.error('Only active customers can be opened in dashboard view.');
            return;
        }
        setImpersonatingId(customer.id);
        try {
            const result = await impersonateCustomer(customer.id);
            if (result?.status === true && result?.data) {
                toast.success(`Opening dashboard as ${customer.fname || ''} ${customer.lname || ''}`.trim() || 'customer');
                startImpersonationSession(result, navigate);
            } else {
                toast.error(result?.message || 'Could not switch to customer view.');
            }
        } catch (err) {
            toast.error(err?.message || 'Could not switch to customer view.');
        } finally {
            setImpersonatingId(null);
        }
    };

    // Handle Status
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Active':
                return 'text-white';
            case 'InActive':
                return 'bg-secondary';
            case 'Pending':
                return 'bg-warning text-dark';
            case 'Suspend':
                return 'bg-danger';
            default:
                return 'bg-light text-dark';
        }
    };

    const getStatusBadgeStyle = (status) => {
        switch (status) {
            case 'Active':
                return {
                    backgroundColor: brandColors.activeGreen,
                };
            default:
                return {};
        }
    };

    const normalize = (value) => String(value || '').toLowerCase();

    const sourceCustomers = isFilterActive ? allCustomers : customers;

    const filteredCustomers = sourceCustomers.filter((customer) => {
        const fullName = `${customer.fname || ''} ${customer.lname || ''}`.trim();
        const planName = customer.plan_detail?.name || '';
        const matchesSearch =
            normalize(fullName).includes(normalize(searchTerm)) ||
            normalize(customer.email).includes(normalize(searchTerm)) ||
            normalize(customer.mobile).includes(normalize(searchTerm)) ||
            normalize(planName).includes(normalize(searchTerm));
        const matchesStatus = statusFilter === 'All' || customer.status === statusFilter;
        const matchesPlan = planFilter === 'All' || planName === planFilter;
        return matchesSearch && matchesStatus && matchesPlan;
    });

    const pagePlanOptions = Array.from(
        new Set(customers.map((customer) => customer.plan_detail?.name).filter(Boolean))
    );
    const planOptions = allPlans.length > 0 ? allPlans : pagePlanOptions;

    const filteredTotalPages = Math.max(1, Math.ceil(filteredCustomers.length / limit));
    const effectiveTotalPages = isFilterActive ? filteredTotalPages : totalPages;
    const paginatedFilteredCustomers = isFilterActive
        ? filteredCustomers.slice((page - 1) * limit, page * limit)
        : filteredCustomers;

    /* Summary cards: never tied to filtered table — stable while user searches/filters. */
    const statsSourceList =
        hasLoadedAllCustomers && Array.isArray(allCustomers) ? allCustomers : customers;
    const summaryOnThisPageCount = customers.length;
    const summaryActiveCount = statsSourceList.filter((c) => c.status === 'Active').length;
    const summaryInactiveCount = statsSourceList.filter((c) => c.status === 'InActive').length;
    const summaryPaidPlansCount = statsSourceList.filter((c) => (c.plan_detail?.price || 0) > 0).length;

    return (
        <div className="container-scroller admin-dashboard-page">
            <SideBar />
            <div className="main-panel">
                <div className="content-wrapper">
                    <HeaderSection />
                    <div className="admin-customers-page">
                    <div className="mt-4 px-4 pb-4 w-100 customers-page-body">
                        <div className="d-flex justify-content-between align-items-center mb-3 customers-header-row">
                            <h3 className="mb-0">Customer Management</h3>
                            <span
                                className="badge px-3 py-2"
                                style={{ backgroundColor: '#e8eefc', color: brandColors.logoBlueDark }}
                            >
                                Total Records: {totalCustomers}
                            </span>
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body py-3" style={{ borderTop: `3px solid ${brandColors.logoBlueDark}` }}>
                                        <p className="text-muted mb-1">On This Page</p>
                                        <h5 className="mb-0" style={{ color: brandColors.logoBlueDark }}>{summaryOnThisPageCount}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body py-3" style={{ borderTop: `3px solid ${brandColors.logoBlue}` }}>
                                        <p className="text-muted mb-1">Active</p>
                                        <h5 className="mb-0" style={{ color: brandColors.logoBlue }}>{summaryActiveCount}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body py-3" style={{ borderTop: `3px solid ${brandColors.logoBlueLight}` }}>
                                        <p className="text-muted mb-1">Inactive</p>
                                        <h5 className="mb-0" style={{ color: brandColors.logoBlueLight }}>{summaryInactiveCount}</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body py-3" style={{ borderTop: `3px solid ${brandColors.logoYellow}` }}>
                                        <p className="text-muted mb-1">Paid Plans</p>
                                        <h5 className="mb-0" style={{ color: brandColors.logoYellow }}>{summaryPaidPlansCount}</h5>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card border-0 shadow-sm mb-3">
                            <div className="card-body" style={{ backgroundColor: brandColors.surfaceSoft }}>
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by name, email, mobile or plan..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <select
                                            className="form-select"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="All">All Status</option>
                                            <option value="Active">Active</option>
                                            <option value="InActive">Inactive</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Suspend">Suspend</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="d-flex gap-2 customers-filter-actions">
                                            <select
                                                className="form-select"
                                                value={planFilter}
                                                onChange={(e) => setPlanFilter(e.target.value)}
                                            >
                                                <option value="All">All Plans</option>
                                                {planOptions.map((plan) => (
                                                    <option key={plan} value={plan}>{plan}</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="btn"
                                                style={{
                                                    border: `1px solid ${brandColors.borderSoft}`,
                                                    color: brandColors.logoBlueDark,
                                                    backgroundColor: '#fff',
                                                }}
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setStatusFilter('All');
                                                    setPlanFilter('All');
                                                }}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {loading || (isFilterActive && allCustomersLoading) ? (
                            <div className="text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive customers-table-wrap">
                                    <table className="table table-hover table-bordered align-middle">
                                        <thead className="text-nowrap" style={{ backgroundColor: brandColors.logoBlueDark }}>
                                            <tr>
                                                <th className="text-white">#</th>
                                                <th className="text-white">Name</th>
                                                <th className="text-white">Email</th>
                                                <th className="text-white">Mobile</th>
                                                <th className="text-white">Plan</th>
                                                <th className="text-white">Apps</th>
                                                <th className="text-white">Status</th>
                                                <th className="text-white">Registered On</th>
                                                <th className="text-white">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCustomers.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-4">
                                                        No customers matched your filters.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedFilteredCustomers.map((customer, index) => {
                                                    const { widgets, appTypes } = customerWidgetTotals(customer);
                                                    return (
                                                    <tr key={customer.id}>
                                                        <td>{(page - 1) * limit + index + 1}</td>
                                                        <td className="fw-semibold">{customer.fname} {customer.lname}</td>
                                                        <td>{customer.email}</td>
                                                        <td>{customer.mobile || 'N/A'}</td>
                                                        <td>
                                                            <span className="badge text-bg-light border">
                                                                {customer.plan_detail?.name || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column gap-1 align-items-start">
                                                                <span className="small text-nowrap">
                                                                    <strong>{widgets}</strong>
                                                                    <span className="text-muted"> widgets</span>
                                                                    <span className="text-muted"> · </span>
                                                                    <strong>{appTypes}</strong>
                                                                    <span className="text-muted"> Apps</span>
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm text-nowrap"
                                                                    style={{
                                                                        border: '1px solid #c5d4f5',
                                                                        color: brandColors.logoBlue,
                                                                        backgroundColor: '#f4f7ff',
                                                                        fontWeight: 600,
                                                                    }}
                                                                    onClick={() => openUsageExplorer(customer)}
                                                                >
                                                                    Apps &amp; widgets
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`badge ${getStatusBadgeClass(customer.status)}`}
                                                                style={getStatusBadgeStyle(customer.status)}
                                                            >
                                                                {customer.status}
                                                            </span>
                                                        </td>
                                                        <td>{formatDate(customer.created_at)}</td>
                                                        <td>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm"
                                                                    style={{
                                                                        border: `1px solid #b7d6ff`,
                                                                        color: '#006dff',
                                                                        backgroundColor: '#F2F8FF',
                                                                    }}
                                                                    onClick={() => {
                                                                        setSelectedCustomer(customer);
                                                                        setShowPlanHistoryModal(true);
                                                                    }}
                                                                >
                                                                    Plan History
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm text-nowrap"
                                                                    style={{
                                                                        border: '1px solid #f0c100',
                                                                        color: '#111',
                                                                        background: 'linear-gradient(131deg, #fe8100 -4.12%, #f5ff00 100%)',
                                                                        fontWeight: 600,
                                                                    }}
                                                                    disabled={impersonatingId === customer.id || customer.status !== 'Active'}
                                                                    title={customer.status !== 'Active' ? 'Customer must be Active' : 'Open customer dashboard (support)'}
                                                                    onClick={() => handleViewAsCustomer(customer)}
                                                                >
                                                                    {impersonatingId === customer.id ? 'Opening…' : 'View as customer'}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                <div className="d-flex justify-content-between align-items-center mt-3 customers-pagination">
                                    <button
                                        className="btn"
                                        style={{
                                            border: `1px solid #b7d6ff`,
                                            color: '#006dff',
                                            backgroundColor: '#F2F8FF',
                                        }}
                                        onClick={handlePrev}
                                        disabled={page <= 1}
                                    >
                                        Previous
                                    </button>
                                    <span className="text-muted">Page {page} of {effectiveTotalPages}</span>
                                    <button
                                        className="btn"
                                        style={{
                                            border: `1px solid #b7d6ff`,
                                            color: '#006dff',
                                            backgroundColor: '#F2F8FF',
                                        }}
                                        onClick={handleNext}
                                        disabled={page >= effectiveTotalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                    </div>
                </div>
            </div>

            {showUsageExplorer && usageCustomer && (
                <div className="modal d-block admin-customers-modal admin-customer-usage-modal" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header">
                                <div>
                                    <h5 className="modal-title mb-0">Apps &amp; widgets</h5>
                                    <p className="text-muted small mb-0 mt-1">
                                        {usageCustomer.fname} {usageCustomer.lname} · {usageCustomer.email}
                                    </p>
                                </div>
                                <button type="button" className="btn-close" onClick={closeUsageExplorer} aria-label="Close" />
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-light border small py-2 mb-3 mb-md-4">
                                    This section shows how many widgets the customer has created for each app. Click{' '}
                                    <strong>Open →</strong> to enter their account and open that app&apos;s widget list.
                                </div>
                                <label className="form-label small text-muted mb-1" htmlFor="admin-usage-app-filter">
                                    Search app by name / type / route
                                </label>
                                <input
                                    id="admin-usage-app-filter"
                                    type="search"
                                    className="form-control form-control-sm mb-3"
                                    placeholder="e.g. testimonials, Whats_App_Chat, /customer/apps/..."
                                    value={usageAppFilter}
                                    onChange={(e) => setUsageAppFilter(e.target.value)}
                                    autoComplete="off"
                                />
                                {usageCustomer.status !== 'Active' && (
                                    <div className="alert alert-warning small py-2 mb-3">
                                        This customer is not Active — opening their app pages is disabled.
                                    </div>
                                )}
                                {usageDetailLoading && (
                                    <div className="text-center py-3 text-muted">Loading widget usage…</div>
                                )}
                                {!usageDetailLoading && usageDetailError && (
                                    <div className="alert alert-warning small py-2 mb-3">{usageDetailError}</div>
                                )}
                                {!usageDetailLoading && usageDetail && (
                                    <div className="mb-4">
                                        <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                                            <span className="badge text-bg-primary">
                                                {usageDetail.widgets_total} widget
                                                {usageDetail.widgets_total === 1 ? '' : 's'} total
                                            </span>
                                            <span className="badge text-bg-secondary">
                                                {usageDetail.app_types_count} Apps
                                            </span>
                                        </div>
                                        {filteredUsageApps.length === 0 ? (
                                            <p className="text-muted small mb-0">
                                                No used app matched your search.
                                            </p>
                                        ) : (
                                            <div
                                                className="list-group mb-0"
                                                style={{ maxHeight: 'min(40vh, 280px)', overflowY: 'auto' }}
                                            >
                                                {filteredUsageApps.map((row) => {
                                                    const meta = getAdminRouteForAppType(row.app_type);
                                                    const openKey = `usage-${row.app_type}`;
                                                    return (
                                                        <div
                                                            key={row.app_type}
                                                            className="list-group-item d-flex flex-wrap justify-content-between align-items-center gap-2 py-2 px-3"
                                                        >
                                                            <div className="min-w-0 flex-grow-1">
                                                                <div className="fw-semibold">{meta.label}</div>
                                                                <div className="small text-muted font-monospace text-truncate">
                                                                    {row.app_type}
                                                                    {!meta.known && (
                                                                        <span className="ms-1 text-warning">(unmapped)</span>
                                                                    )}
                                                                </div>
                                                                {meta.customerPath && (
                                                                    <div
                                                                        className="small text-muted font-monospace text-truncate"
                                                                        title={meta.customerPath}
                                                                    >
                                                                        {meta.customerPath}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                                <span className="badge text-bg-dark">{row.widget_count}</span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm text-nowrap"
                                                                    style={{
                                                                        border: '1px solid #c5d4f5',
                                                                        color: '#1f3f95',
                                                                        backgroundColor: '#f4f7ff',
                                                                        fontWeight: 600,
                                                                        borderRadius: '4px',
                                                                        padding: '2px 10px',
                                                                    }}
                                                                    disabled={
                                                                        usageCustomer.status !== 'Active' ||
                                                                        usageOpeningAppKey !== null ||
                                                                        !meta.customerPath
                                                                    }
                                                                    title={
                                                                        !meta.customerPath
                                                                            ? 'Add mapping in adminAppTypeToCustomerRoute.js'
                                                                            : undefined
                                                                    }
                                                                    onClick={() =>
                                                                        handleOpenCustomerAppPage({
                                                                            key: openKey,
                                                                            label: meta.label,
                                                                            customerPath: meta.customerPath,
                                                                        })
                                                                    }
                                                                >
                                                                    {usageOpeningAppKey === openKey ? 'Opening…' : 'Open →'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                                    <h6 className="text-muted small text-uppercase mb-0">All apps (shortcuts)</h6>
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        style={{
                                            border: '1px solid #cfd6e4',
                                            color: '#2f3b50',
                                            backgroundColor: '#f8f9fc',
                                            borderRadius: '4px',
                                            fontWeight: 600,
                                            padding: '3px 10px',
                                        }}
                                        onClick={() => setShowAllShortcutApps((v) => !v)}
                                    >
                                        {showAllShortcutApps ? 'Hide' : 'Show'} shortcuts
                                    </button>
                                </div>
                                {showAllShortcutApps && (
                                    <>
                                        <p className="small text-muted mb-2">
                                            Quick navigation to any app page, even when no widgets exist yet (useful
                                            for support and setup).
                                        </p>
                                        {filteredAppShortcuts.length === 0 ? (
                                            <p className="text-muted small mb-0">No shortcut matched your search.</p>
                                        ) : (
                                            <div
                                                className="list-group admin-customer-app-shortcut-list"
                                                style={{ maxHeight: 'min(35vh, 260px)', overflowY: 'auto' }}
                                            >
                                                {filteredAppShortcuts.map((app) => (
                                                    <button
                                                        key={app.key}
                                                        type="button"
                                                        className="list-group-item list-group-item-action text-start py-2 px-3"
                                                        disabled={
                                                            usageCustomer.status !== 'Active' || usageOpeningAppKey !== null
                                                        }
                                                        onClick={() => handleOpenCustomerAppPage(app)}
                                                    >
                                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                                            <span className="fw-semibold">{app.label}</span>
                                                            <span
                                                                className="small text-nowrap"
                                                                style={{
                                                                    color: '#1f3f95',
                                                                    border: '1px solid #c5d4f5',
                                                                    backgroundColor: '#f4f7ff',
                                                                    borderRadius: '4px',
                                                                    padding: '2px 8px',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {usageOpeningAppKey === app.key ? 'Opening…' : 'Open →'}
                                                            </span>
                                                        </div>
                                                        <div
                                                            className="small text-muted font-monospace text-truncate mt-1"
                                                            title={app.customerPath}
                                                        >
                                                            {app.customerPath}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn px-4"
                                    style={{
                                        border: '1px solid #cfd6e4',
                                        color: '#2f3b50',
                                        backgroundColor: '#f8f9fc',
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                    }}
                                    onClick={closeUsageExplorer}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPlanHistoryModal && selectedCustomer && (
                <div className="modal d-block admin-customers-modal" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Plan History - {selectedCustomer.fname} {selectedCustomer.lname}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowPlanHistoryModal(false);
                                        setPlanHistoryRows([]);
                                        setPlanHistoryError(null);
                                    }}
                                />
                            </div>
                            <div className="modal-body">
                                <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
                                    <span><strong>Email:</strong> {selectedCustomer.email}</span>
                                    <span><strong>Current Plan:</strong> {selectedCustomer.plan_detail?.name || 'N/A'}</span>
                                </div>
                                {planHistoryLoading && (
                                    <div className="text-center py-4 text-muted">Loading plan history…</div>
                                )}
                                {!planHistoryLoading && planHistoryError && (
                                    <div className="alert alert-warning py-2 mb-2">{planHistoryError}</div>
                                )}
                                {!planHistoryLoading && !planHistoryError && planHistoryRows.length === 0 && (
                                    <p className="text-muted mb-0">No plan payment records for this customer yet.</p>
                                )}
                                {!planHistoryLoading && planHistoryRows.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Txn ID</th>
                                                <th>Plan</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Started On</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {planHistoryRows.map((item) => {
                                                const ok = planHistoryStatusSuccess(item.payment_status);
                                                const pend = String(item.payment_status || '').toLowerCase().includes('pending');
                                                return (
                                                    <tr key={item.id ?? item.txn_id}>
                                                        <td className="text-break">{item.txn_id || '—'}</td>
                                                        <td>{item.plan_name || '—'}</td>
                                                        <td>{item.amount_display ?? item.amount ?? '—'}</td>
                                                        <td>
                                                            <span
                                                                className={`badge ${
                                                                    ok ? 'bg-success' : pend ? 'bg-warning text-dark' : 'bg-secondary'
                                                                }`}
                                                            >
                                                                {item.payment_status || '—'}
                                                            </span>
                                                        </td>
                                                        <td>{formatDate(item.created_at)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                )}
                                <p className="text-muted small mb-0 mt-3">
                                    Records from plan payments (e.g. Stripe). Older activity may not appear if it was never logged.
                                </p>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn px-4 admin-customers-modal-close-btn"
                                    style={{
                                        border: '1px solid #f0c100',
                                        color: '#111',
                                        background: 'linear-gradient(131deg, #fe8100 -4.12%, #f5ff00 100%)',
                                        fontWeight: 700,
                                        borderRadius: '8px',
                                    }}
                                    onClick={() => {
                                        setShowPlanHistoryModal(false);
                                        setPlanHistoryRows([]);
                                        setPlanHistoryError(null);
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
